import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { buildSectionSelector, morphSection, normalizeSectionId, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);
      const applyEmpty = () => {
        this.replaceChildren(clone);
        this.#syncCartDrawerEmptyState();
      };

      // Defer drawer swap until after the remove click finishes bubbling, so the dialog
      // does not treat the detached remove button as an outside click and close.
      if (this.isDrawer) {
        queueMicrotask(applyEmpty);
      } else if (prefersReducedMotion()) {
        applyEmpty();
      } else {
        startViewTransition(applyEmpty, ['empty-cart-page']);
      }

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then(async (responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        const giftPrepare =
          window.LOPGift && typeof window.LOPGift.prepare === 'function' ? window.LOPGift.prepare() : null;

        if (giftPrepare) {
          await giftPrepare;
          const cart = await fetch('/cart.js', { credentials: 'same-origin' })
            .then((response) => response.json())
            .catch(() => null);
          const itemCount = cart?.item_count ?? newCartItemCount;

          /* Sin sections: el HTML de change.js es anterior al regalo y deja el drawer atrasado. */
          this.dispatchEvent(
            new CartUpdateEvent(parsedResponseText, this.sectionId, {
              itemCount,
              source: 'lop-gift',
              lopGiftSynced: true,
            })
          );

          if (this.isDrawer) {
            if (!itemCount) this.showDrawerEmpty();
            else await this.refreshDrawer(undefined, { fromAddToCart: true, expectedItemCount: itemCount });
          }
        } else {
          this.dispatchEvent(
            new CartUpdateEvent(parsedResponseText, this.sectionId, {
              itemCount: newCartItemCount,
              source: 'cart-items-component',
              sections: parsedResponseText.sections,
            })
          );

          const sectionHtml = this.#getSectionHtmlFromResponse(parsedResponseText.sections);
          if (sectionHtml) await this.#applySectionUpdate(sectionHtml);
        }

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = async (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const sectionHtml = this.#getSectionHtmlFromResponse(event.detail.data?.sections);

    // product-form y el +/- del drawer refrescan después de sincronizar el regalo.
    if (this.isDrawer && (event.detail?.data?.source === 'product-form-component' || event.detail?.data?.lopGiftSynced)) {
      return;
    }

    if (sectionHtml) {
      if (this.#shouldHydrateDrawerAfterAdd(event)) {
        await this.#hydrateDrawerAfterAdd(sectionHtml);
        return;
      }

      if (this.isDrawer && this.#shouldIgnoreStaleEmptySection(sectionHtml)) {
        return;
      }

      await this.#applySectionUpdate(sectionHtml);
    } else if (this.isDrawer && !this.querySelector('.cart-form')) {
      await this.#refreshDrawerFromServer();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Resolves section HTML from cart/add or cart/change responses.
   * @param {Record<string, string> | undefined} sections
   * @returns {string | undefined}
   */
  #getSectionHtmlFromResponse(sections) {
    if (!sections) return undefined;

    if (sections[this.sectionId]) return sections[this.sectionId];

    const normalizedId = normalizeSectionId(this.sectionId);

    for (const [key, html] of Object.entries(sections)) {
      if (key === normalizedId || normalizeSectionId(key) === normalizedId) {
        return html;
      }
    }

    return undefined;
  }

  /**
   * Applies section HTML to this component (full morph or drawer swap when trees differ).
   * @param {string} sectionHtml
   */
  async #applySectionUpdate(sectionHtml) {
    if (!sectionHtml) return;

    if (this.isDrawer) {
      const isCurrentlyEmpty = !this.querySelector('.cart-form');
      const parsed = this.#parseDrawerSection(sectionHtml);
      const willBeEmpty = this.#willDrawerSectionBeEmpty(parsed, sectionHtml);

      if (willBeEmpty === undefined) {
        morphSection(this.sectionId, sectionHtml, 'hydration');
        this.#syncCartDrawerEmptyState();
        this.#updateCartQuantitySelectorButtonStates();
        return;
      }

      if (isCurrentlyEmpty && willBeEmpty) {
        const cart = await fetch('/cart.js', { credentials: 'same-origin' })
          .then((response) => response.json())
          .catch(() => null);

        if (cart?.item_count) {
          await this.refreshDrawer(undefined, { fromAddToCart: true });
          return;
        }

        this.#syncDrawerFromSectionHtml(sectionHtml);
        return;
      }

      if (isCurrentlyEmpty && !willBeEmpty) {
        this.#syncDrawerFromSectionHtml(sectionHtml);
        if (!this.querySelector('.cart-form')) {
          void this.#ensureDrawerHasCartForm();
        }
        return;
      }

      if (!isCurrentlyEmpty && willBeEmpty) {
        this.#syncDrawerFromSectionHtmlIfCartIsEmpty(sectionHtml);
        return;
      }
    }

    morphSection(this.sectionId, sectionHtml, this.isDrawer ? 'hydration' : 'full');
    this.#syncCartDrawerEmptyState();
    this.#updateCartQuantitySelectorButtonStates();
  }

  /**
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   * @returns {boolean}
   */
  #shouldHydrateDrawerAfterAdd(event) {
    return (
      this.isDrawer &&
      !this.querySelector('.cart-form') &&
      !event.detail?.data?.didError &&
      event.target !== this
    );
  }

  /**
   * Fills the drawer after add-to-cart (sections response is often stale/empty).
   * @param {string} sectionHtml
   */
  async #hydrateDrawerAfterAdd(sectionHtml) {
    if (this.#swapFilledDrawerFromSectionHtml(sectionHtml)) return;

    await this.#refreshDrawerFromServer();
  }

  /**
   * Ignores section HTML that would clear a drawer which already has line items.
   * @param {string} sectionHtml
   * @returns {boolean}
   */
  #shouldIgnoreStaleEmptySection(sectionHtml) {
    if (!this.querySelector('.cart-form')) return false;

    const parsed = this.#parseDrawerSection(sectionHtml);
    return this.#willDrawerSectionBeEmpty(parsed, sectionHtml) === true;
  }

  /**
   * Swaps drawer markup from section HTML when the cart has items.
   * @param {string} sectionHtml
   * @returns {boolean}
   */
  #applyDrawerSectionHtml(sectionHtml) {
    if (this.#willDrawerSectionBeEmpty(this.#parseDrawerSection(sectionHtml), sectionHtml)) {
      return false;
    }

    return this.#swapDrawerInnerFromSectionHtml(sectionHtml) || this.#swapFilledDrawerFromSectionHtml(sectionHtml);
  }

  /**
   * Swaps drawer children when the parsed section contains a filled cart (skips empty-state heuristic).
   * @param {string} sectionHtml
   * @returns {boolean}
   */
  #swapFilledDrawerFromSectionHtml(sectionHtml) {
    const parsed = this.#parseDrawerSection(sectionHtml);

    if (!parsed.newCartItems?.querySelector('.cart-form, .cart-drawer__content--filled')) {
      return false;
    }

    if (!this.#swapDrawerFromParsed(parsed)) {
      return false;
    }

    this.#syncCartDrawerEmptyState();
    this.#updateCartQuantitySelectorButtonStates();
    return true;
  }

  /**
   * Pinta el drawer vacío. refreshDrawer no lo hace: con item_count 0 corta antes de traer el HTML.
   */
  showDrawerEmpty() {
    const template = document.getElementById('empty-cart-template');
    if (!(template instanceof HTMLTemplateElement)) return;

    this.replaceChildren(document.importNode(template.content, true));
    this.#syncCartDrawerEmptyState();
  }

  /**
   * Public API: refresh drawer markup after add-to-cart (called from product-form).
   * @param {Record<string, string> | undefined} sections
   * @param {{ fromAddToCart?: boolean, expectedItemCount?: number }} [options]
   * @returns {Promise<boolean>}
   */
  async refreshDrawer(sections, options) {
    if (!this.isDrawer) return false;

    const { fromAddToCart = false, expectedItemCount = 0 } = options ?? {};

    sectionRenderer.invalidateAllSectionUrls(this.sectionId);

    const cart = await this.#fetchCartWithRetry(fromAddToCart, expectedItemCount);

    if (!cart?.item_count) {
      this.syncCartDrawerEmptyState();
      return false;
    }

    const sectionHtml = sections ? this.#getSectionHtmlFromResponse(sections) : undefined;
    const sectionMatches =
      !expectedItemCount || this.#sectionItemCount(sectionHtml) === expectedItemCount;

    if (sectionHtml && sectionMatches && this.#applyDrawerSectionHtml(sectionHtml) && this.#drawerHasLineItems()) {
      this.syncCartDrawerEmptyState();
      this.#markHeaderHydratedWhenFilled();
      return true;
    }

    const refreshed = await this.#refreshDrawerFromServer(expectedItemCount);
    this.syncCartDrawerEmptyState();
    if (refreshed) this.#markHeaderHydratedWhenFilled();
    return refreshed;
  }

  /** Marks header section hydrated only after the drawer has line items. */
  #markHeaderHydratedWhenFilled() {
    if (!this.#drawerHasLineItems()) return;

    const section = document.getElementById(buildSectionSelector(this.sectionId));
    if (section instanceof HTMLElement) {
      section.dataset.hydrated = 'true';
    }
  }

  /**
   * @param {boolean} fromAddToCart
   * @param {number} expectedItemCount
   * @returns {Promise<{ item_count: number } | null>}
   */
  async #fetchCartWithRetry(fromAddToCart, expectedItemCount) {
    const maxAttempts = fromAddToCart ? 4 : 1;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const cart = await fetch('/cart.js', { credentials: 'same-origin' })
        .then((response) => response.json())
        .catch(() => null);

      if (cart?.item_count) return cart;

      if (fromAddToCart && expectedItemCount > 0 && attempt < maxAttempts - 1) {
        await new Promise((resolve) => {
          window.setTimeout(resolve, 80 * (attempt + 1));
        });
      }
    }

    return null;
  }

  /**
   * @returns {boolean}
   */
  #drawerHasLineItems() {
    return Boolean(document.querySelector('cart-items-component[data-drawer] .cart-form'));
  }

  /**
   * @param {string | undefined} sectionHtml
   * @returns {number | null}
   */
  #sectionItemCount(sectionHtml) {
    if (!sectionHtml) return null;
    const node = new DOMParser().parseFromString(sectionHtml, 'text/html').querySelector('[ref="cartItemCount"]');
    if (!node) return null;
    const value = parseInt(node.textContent, 10);
    return Number.isNaN(value) ? null : value;
  }

  /**
   * @param {number} [expectedItemCount]
   * @returns {Promise<boolean>}
   */
  async #refreshDrawerFromServer(expectedItemCount = 0) {
    const cart = await this.#fetchCartWithRetry(true, 1);

    if (!cart?.item_count) return false;

    const urls = [
      new URL('/cart', window.location.origin),
      new URL(window.location.pathname + window.location.search, window.location.origin),
      new URL(window.location.href),
    ];

    const maxAttempts = expectedItemCount ? 8 : 6;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      for (const url of urls) {
        sectionRenderer.invalidateSectionCache(this.sectionId, url);

        const sectionHtml = await sectionRenderer.getSectionHTML(this.sectionId, false, url);
        if (expectedItemCount && this.#sectionItemCount(sectionHtml) !== expectedItemCount) continue;
        if (this.#applyDrawerSectionHtml(sectionHtml) && this.#drawerHasLineItems()) {
          return true;
        }
      }

      if (attempt < maxAttempts - 1) {
        await new Promise((resolve) => {
          window.setTimeout(resolve, 120 * (attempt + 1));
        });
      }
    }

    return false;
  }

  /**
   * Replaces `.cart-drawer__inner` when cart-items swap is not enough.
   * @param {string} sectionHtml
   * @returns {boolean}
   */
  #swapDrawerInnerFromSectionHtml(sectionHtml) {
    const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const newInner =
      doc.querySelector('[data-hydration-key="cart-drawer-inner"]') ?? doc.querySelector('.cart-drawer__inner');
    const currentInner = document.querySelector('.cart-drawer__inner');

    if (!(newInner instanceof HTMLElement) || !(currentInner instanceof HTMLElement)) return false;
    if (!newInner.querySelector('.cart-form, .cart-drawer__content--filled')) return false;

    currentInner.replaceChildren();
    while (newInner.firstChild) {
      currentInner.appendChild(newInner.firstChild);
    }

    this.syncCartDrawerEmptyState();
    this.#updateCartQuantitySelectorButtonStates();
    return Boolean(document.querySelector('cart-items-component[data-drawer] .cart-form'));
  }

  /**
   * @param {string} sectionHtml
   * @returns {Element | null | undefined}
   */
  #getDrawerCartItemsFromSectionHtml(sectionHtml) {
    const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const sectionRoot =
      doc.getElementById(buildSectionSelector(this.sectionId)) ?? doc.querySelector('[id^="shopify-section-"]');

    return sectionRoot?.querySelector('cart-items-component[data-drawer]');
  }

  /**
   * @param {string} sectionHtml
   * @returns {number | undefined}
   */
  #getItemCountFromSectionHtml(sectionHtml) {
    const drawerCartItems = this.#getDrawerCartItemsFromSectionHtml(sectionHtml);
    if (!drawerCartItems) return undefined;

    const countText = drawerCartItems.querySelector('[ref="cartItemCount"]')?.textContent?.trim();

    if (countText !== undefined && countText !== '') {
      const count = parseInt(countText, 10);
      if (!Number.isNaN(count)) return count;
    }

    if (drawerCartItems.querySelector('.cart-drawer__content--filled, .cart-form')) {
      return 1;
    }

    if (drawerCartItems.querySelector('.cart-drawer__content--empty')) {
      return 0;
    }

    return undefined;
  }

  /**
   * @param {{ newCartItems: Element | null | undefined }} parsed
   * @param {string} sectionHtml
   * @returns {boolean | undefined} `undefined` when the drawer subtree cannot be parsed.
   */
  #willDrawerSectionBeEmpty(parsed, sectionHtml) {
    const { newCartItems } = parsed;

    if (newCartItems?.querySelector('.cart-form, .cart-drawer__content--filled')) {
      return false;
    }

    const itemCount = this.#getItemCountFromSectionHtml(sectionHtml);
    if (itemCount !== undefined) return itemCount === 0;

    if (!newCartItems) return undefined;

    return Boolean(newCartItems.querySelector('.cart-drawer__content--empty'));
  }

  /**
   * @param {string} sectionHtml
   * @returns {{ newCartItems: Element | null | undefined, newDialog: Element | null | undefined }}
   */
  #parseDrawerSection(sectionHtml) {
    const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const sectionRoot =
      doc.getElementById(buildSectionSelector(this.sectionId)) ??
      doc.querySelector(`[id^="shopify-section-"]`) ??
      doc.body;

    const newCartItems =
      sectionRoot.querySelector('cart-items-component[data-drawer]') ??
      sectionRoot.querySelector('cart-items-component') ??
      doc.querySelector('cart-items-component[data-drawer]');

    const newDialog =
      sectionRoot.querySelector('.cart-drawer__dialog') ?? doc.querySelector('.cart-drawer__dialog');

    return {
      newCartItems,
      newDialog,
    };
  }

  /**
   * Swaps drawer cart-items children and dialog attributes from parsed section HTML.
   * @param {{ newCartItems: Element | null | undefined, newDialog: Element | null | undefined }} parsed
   * @returns {boolean}
   */
  #swapDrawerFromParsed(parsed) {
    const { newCartItems, newDialog } = parsed;

    if (!newCartItems) return false;

    const drawerCartItems = document.querySelector('cart-items-component[data-drawer]');
    const target =
      drawerCartItems instanceof CartItemsComponent ? drawerCartItems : this.isDrawer ? this : null;

    if (!target) return false;

    target.replaceChildren();
    while (newCartItems.firstChild) {
      target.appendChild(newCartItems.firstChild);
    }

    const dialog = document.querySelector('.cart-drawer__dialog');
    if (dialog instanceof HTMLElement && newDialog instanceof HTMLElement) {
      const wasOpen = dialog.open;
      dialog.setAttribute('aria-labelledby', newDialog.getAttribute('aria-labelledby') ?? 'cart-drawer-heading');

      if (newDialog.hasAttribute('cart-summary-sticky')) {
        dialog.setAttribute('cart-summary-sticky', newDialog.getAttribute('cart-summary-sticky') ?? '');
      } else {
        dialog.removeAttribute('cart-summary-sticky');
      }

      if (wasOpen && !dialog.open) {
        dialog.showModal();
      }
    }

    this.syncCartDrawerEmptyState();
    return true;
  }

  /**
   * Replaces cart drawer markup when empty ↔ filled trees differ (morph is unreliable).
   * Drawer swaps are instant so empty/filled states are not double-animated.
   * @param {string} sectionHtml
   */
  /**
   * @param {string} sectionHtml
   */
  async #syncDrawerFromSectionHtmlIfCartIsEmpty(sectionHtml) {
    const cart = await fetch('/cart.js', { credentials: 'same-origin' })
      .then((response) => response.json())
      .catch(() => null);

    if (cart?.item_count) return;

    this.#syncDrawerFromSectionHtml(sectionHtml);
  }

  /**
   * @param {string} sectionHtml
   */
  #syncDrawerFromSectionHtml(sectionHtml) {
    const parsed = this.#parseDrawerSection(sectionHtml);
    const isCurrentlyEmpty = !this.querySelector('.cart-form');
    const willBeEmpty = this.#willDrawerSectionBeEmpty(parsed, sectionHtml);
    const morphMode = isCurrentlyEmpty !== willBeEmpty ? 'full' : 'hydration';

    if (!this.#swapDrawerFromParsed(parsed)) {
      morphSection(this.sectionId, sectionHtml, morphMode);
    }

    this.#syncCartDrawerEmptyState();
    this.#updateCartQuantitySelectorButtonStates();
  }

  /**
   * Refetches the header section when add-to-cart HTML did not hydrate the drawer.
   */
  async #ensureDrawerHasCartForm() {
    if (this.querySelector('.cart-form')) return;

    await this.#refreshDrawerFromServer();
    this.#syncCartDrawerEmptyState();
  }

  /**
   * Toggles cart drawer empty UI (dialog class) after section morph.
   */
  syncCartDrawerEmptyState() {
    const cartItems = document.querySelector('cart-items-component[data-drawer]');
    const dialog = document.querySelector('.cart-drawer__dialog');
    if (!(cartItems instanceof HTMLElement) || !(dialog instanceof HTMLElement)) return;

    const isEmpty = !cartItems.querySelector('.cart-form');
    dialog.classList.toggle('cart-drawer--empty', isEmpty);
    dialog.setAttribute('cart-summary-sticky', isEmpty ? 'false' : 'true');
  }

  #syncCartDrawerEmptyState() {
    this.syncCartDrawerEmptyState();
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
